import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Plus, Sparkles, CreditCard, LogOut, Loader2, Bot, ExternalLink, X,
  PanelRight, FileText, Download, Code2, Rocket, Send, ReceiptText, Globe,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import BuyCreditsModal from '../components/BuyCreditsModal';
import Logo from '../components/Logo';
import { workspaceAPI, prototypeAPI, buildAPI, aiAPI, apiErrorMessage } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EXAMPLES = [
  'A WhatsApp ordering bot for my restaurant with menu, cart and payments',
  'A SaaS dashboard to track my field sales team with maps and reports',
  'A booking website for my clinic with patient login and reminders',
];

const projectTitle = (p) => {
  const build = p.builds?.[0];
  if (build?.project_name && build.status === 'ready') return build.project_name;
  if (p.lead.project_type) return p.lead.project_type;
  const d = p.lead.description || 'New project';
  return d.length > 42 ? `${d.slice(0, 42)}…` : d;
};

const previewOf = (p) => {
  const ready = (p?.prototypes || []).filter((x) => x.status === 'ready');
  const full = ready.find((x) => !x.is_teaser);
  const teaser = ready.find((x) => x.is_teaser);
  const proto = full || teaser;
  if (!proto) return null;
  const url = proto.preview_path ? `${BACKEND_URL}${proto.preview_path}` : proto.preview_url;
  return { url, isTeaser: !!proto.is_teaser, id: proto.id };
};

const activityOf = (p) => {
  if (!p) return null;
  const { lead } = p;
  const build = p.builds?.[0];
  const teaser = (p.prototypes || []).find((x) => x.is_teaser);
  const full = (p.prototypes || []).find((x) => !x.is_teaser);
  if (build?.status === 'generating') {
    return { label: build.current_step || 'Build agents working…', progress: build.progress || 0 };
  }
  if (build?.test_status === 'generating') {
    return { label: 'QA Agent — reviewing every file of your build…' };
  }
  if (full?.status === 'generating') {
    return { label: 'Prototype Agent — building your full multi-page prototype… (~2-3 min)' };
  }
  if (teaser?.status === 'generating') {
    return { label: 'Prototype Agent — building your free teaser prototype… (~1-2 min)' };
  }
  if (lead?.proposal_status === 'generating') {
    return { label: 'Proposal Agent — drafting scope, timeline & costs…' };
  }
  return null;
};

const stateKeyOf = (p) => {
  if (!p) return '';
  const b = p.builds?.[0] || {};
  const t = (p.prototypes || []).find((x) => x.is_teaser) || {};
  const f = (p.prototypes || []).find((x) => !x.is_teaser) || {};
  return [p.lead.proposal_status, !!p.lead.ai_proposal, t.status, f.status, b.status, b.test_status, b.release_status].join('|');
};

function ActionButton({ actionId, label, project, running, onRun }) {
  // Hide if the action no longer applies to current state
  const b = project?.builds?.[0];
  const t = (project?.prototypes || []).find((x) => x.is_teaser);
  const f = (project?.prototypes || []).find((x) => !x.is_teaser);
  const done = {
    generate_proposal: !!project?.lead?.ai_proposal || project?.lead?.proposal_status === 'generating',
    teaser: ['ready', 'generating'].includes(t?.status),
    full_prototype: ['ready', 'generating'].includes(f?.status),
    dev_build: ['ready', 'generating'].includes(b?.status),
    run_tests: b?.status !== 'ready' || ['ready', 'generating'].includes(b?.test_status),
    release: b?.status !== 'ready' || b?.release_status === 'ready',
  }[actionId];
  if (done) return null;
  return (
    <Button
      size="sm"
      className="mt-3 gap-2 font-semibold text-white"
      style={{ background: 'var(--primary-accent)' }}
      disabled={running}
      onClick={() => onRun(actionId)}
      data-testid={`action-btn-${actionId}`}
    >
      {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
      {label}
    </Button>
  );
}

export default function WorkspaceChatPage() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser, logout } = useAuth();
  const [projects, setProjects] = useState([]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [runningAction, setRunningAction] = useState(null);
  const [showBuy, setShowBuy] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const bottomRef = useRef(null);
  const prevStateRef = useRef('');

  const project = projects.find((p) => p.lead.id === leadId) || null;
  const preview = previewOf(project);
  const activity = activityOf(project);
  const build = project?.builds?.[0];

  const loadProjects = useCallback(async () => {
    try {
      const res = await workspaceAPI.getProjects();
      setProjects(res.data.projects || []);
    } catch { /* ignore */ }
  }, []);

  const loadMessages = useCallback(async () => {
    if (!leadId) { setMessages([]); return; }
    try {
      const res = await workspaceAPI.getMessages(leadId);
      setMessages(res.data.messages || []);
    } catch { setMessages([]); }
  }, [leadId]);

  useEffect(() => { loadProjects(); }, [loadProjects]);
  useEffect(() => { loadMessages(); prevStateRef.current = ''; }, [loadMessages]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, activity, sending]);

  // Poll while agents are working; announce completions in the thread
  useEffect(() => {
    if (!activity) return undefined;
    const t = setInterval(loadProjects, 5000);
    return () => clearInterval(t);
  }, [activity, loadProjects]);

  useEffect(() => {
    if (!project) return;
    const key = stateKeyOf(project);
    const prev = prevStateRef.current;
    prevStateRef.current = key;
    if (!prev || prev === key) return;
    const announce = async (text) => {
      try {
        const res = await workspaceAPI.postEvent(project.lead.id, text);
        setMessages((m) => [...m, res.data]);
      } catch { /* ignore */ }
      refreshUser();
    };
    const [pPs, pProp, pT, pF, pB, pTest, pRel] = prev.split('|');
    const b = project.builds?.[0];
    const t = (project.prototypes || []).find((x) => x.is_teaser);
    const f = (project.prototypes || []).find((x) => !x.is_teaser);
    if (pPs === 'generating' && project.lead.proposal_status === 'ready') announce('Proposal ready — scope, timeline & costs are prepared. Open it from the artifacts bar below.');
    if (pPs === 'generating' && project.lead.proposal_status === 'failed') announce('Proposal generation failed. Ask me to retry.');
    if (pT === 'generating' && t?.status === 'ready') { announce('Teaser prototype is live — opening the preview panel.'); setShowPreview(true); }
    if (pT === 'generating' && t?.status === 'failed') announce('Teaser generation failed. Ask me to retry.');
    if (pF === 'generating' && f?.status === 'ready') { announce('Full interactive prototype is live — opening the preview panel.'); setShowPreview(true); }
    if (pF === 'generating' && f?.status === 'failed') announce('Full prototype generation failed. Ask me to retry.');
    if (pB === 'generating' && b?.status === 'ready') announce(`Development build complete — ${b.files_created?.length || 'all'} source files written (React + FastAPI + MongoDB). Browse or download from the artifacts bar.`);
    if (pB === 'generating' && b?.status === 'failed') announce('Development build failed. Ask me to retry.');
    if (pTest === 'generating' && b?.test_status === 'ready') announce(`QA complete — quality score ${b.test_report?.quality_score ?? '—'}/100. Full report is in Pipeline & files.`);
    if (pTest === 'generating' && b?.test_status === 'failed') announce('QA run failed. Ask me to retry.');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects]);

  const sendMessage = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || sending) return;
    setInput('');
    setMessages((m) => [...m, { id: `tmp-${Date.now()}`, role: 'user', content: text }]);
    setSending(true);
    try {
      const res = await workspaceAPI.chat(leadId || null, text);
      setMessages((m) => [...m, {
        id: `tmp-a-${Date.now()}`, role: 'assistant',
        content: res.data.reply, action: res.data.action?.id || null, actionLabel: res.data.action?.label,
      }]);
      if (res.data.created_project) {
        await loadProjects();
        navigate(`/workspace/${res.data.lead_id}`);
      }
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Message failed — please retry'));
    } finally {
      setSending(false);
    }
  };

  const runAction = async (actionId) => {
    if (!project) return;
    setRunningAction(actionId);
    const id = project.lead.id;
    try {
      const started = {
        generate_proposal: ['Proposal Agent engaged — drafting your scope, timeline & commercial proposal.', () => workspaceAPI.generateProposal(id)],
        teaser: ['Prototype Agent engaged — building your free teaser prototype.', () => prototypeAPI.generateTeaser(id)],
        full_prototype: ['Prototype Agent engaged — building the full multi-page interactive prototype.', () => prototypeAPI.generateFull(id)],
        dev_build: ['Engineering agents engaged — Architect, Backend, UI and Release agents are writing your production codebase (~10 min).', () => buildAPI.generate(id)],
        run_tests: ['QA Agent engaged — reviewing your codebase and preparing the test report.', () => buildAPI.runTests(build.id)],
        release: ['Release Agent engaged — packaging your hosting-ready release.', () => buildAPI.packageRelease(build.id)],
      }[actionId];
      if (!started) return;
      await started[1]();
      const ev = await workspaceAPI.postEvent(id, started[0]);
      setMessages((m) => [...m, ev.data]);
      await loadProjects();
      await refreshUser();
      if (actionId === 'release') {
        const ev2 = await workspaceAPI.postEvent(id, 'Release package ready — download it from the artifacts bar.');
        setMessages((m) => [...m, ev2.data]);
        await loadProjects();
      }
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Could not start that — please retry'));
      if (e.response?.status === 402) setShowBuy(true);
    } finally {
      setRunningAction(null);
    }
  };

  const downloadBlob = (res, filename) => {
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const lastAssistantIdx = [...messages].map((m) => m.role).lastIndexOf('assistant');

  return (
    <div className="h-screen flex overflow-hidden" style={{ background: 'var(--background)', color: 'var(--text-primary)' }} data-testid="workspace-chat-page">
      {/* ============ SIDEBAR ============ */}
      <aside className="hidden md:flex w-72 flex-col shrink-0" style={{ background: 'var(--dark-section-bg)', color: 'var(--dark-section-text)' }} data-testid="chat-sidebar">
        <div className="p-4 flex items-center gap-2.5">
          <Link to="/" className="flex items-center gap-2.5">
            <Logo size={30} isDark />
            <span className="font-heading font-semibold">XceedAgents</span>
          </Link>
        </div>
        <div className="px-3">
          <Button
            variant="outline"
            className="w-full justify-start gap-2 border-0 text-sm font-semibold"
            style={{ background: 'rgba(234,88,12,0.15)', color: '#FB923C' }}
            onClick={() => navigate('/workspace')}
            data-testid="new-project-btn"
          >
            <Plus className="w-4 h-4" /> New Project
          </Button>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 mt-4 space-y-0.5">
          <p className="px-2 pb-1 text-[10px] font-bold uppercase tracking-widest" style={{ color: '#64748B' }}>Projects</p>
          {projects.map((p) => {
            const busy = !!activityOf(p);
            const active = p.lead.id === leadId;
            return (
              <button
                key={p.lead.id}
                onClick={() => navigate(`/workspace/${p.lead.id}`)}
                className="w-full text-left px-3 py-2.5 rounded-lg text-sm flex items-center gap-2 transition-colors"
                style={{
                  background: active ? 'rgba(255,255,255,0.08)' : 'transparent',
                  color: active ? '#F8FAFC' : '#94A3B8',
                }}
                data-testid={`project-item-${p.lead.id}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${busy ? 'animate-pulse' : ''}`} style={{ background: busy ? '#F59E0B' : '#334155' }} />
                <span className="truncate capitalize">{projectTitle(p)}</span>
              </button>
            );
          })}
          {projects.length === 0 && (
            <p className="px-3 py-2 text-xs" style={{ color: '#64748B' }}>No projects yet — describe your idea to start.</p>
          )}
        </nav>
        <div className="p-3 border-t space-y-2" style={{ borderColor: 'var(--dark-section-border)' }}>
          <div className="flex items-center justify-between px-1">
            <span className="flex items-center gap-1.5 text-sm font-semibold" style={{ color: '#FB923C' }} data-testid="sidebar-credits">
              <Sparkles className="w-4 h-4" /> {user?.credits ?? 0} credits
            </span>
            <Link to="/workspace/billing" className="text-xs hover:underline flex items-center gap-1" style={{ color: '#94A3B8' }} data-testid="sidebar-billing-link">
              <ReceiptText className="w-3.5 h-3.5" /> Billing
            </Link>
          </div>
          <Button
            className="w-full gap-2 text-sm font-semibold text-white"
            style={{ background: 'var(--primary-accent)' }}
            onClick={() => setShowBuy(true)}
            data-testid="sidebar-buy-credits"
          >
            <CreditCard className="w-4 h-4" /> Buy Credits
          </Button>
          <div className="flex items-center justify-between px-1 pt-1">
            <span className="text-xs truncate" style={{ color: '#94A3B8' }}>{user?.name}</span>
            <button onClick={() => { logout(); navigate('/'); }} title="Sign out" data-testid="sidebar-logout" style={{ color: '#64748B' }}>
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* ============ MAIN CHAT ============ */}
      <main className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="flex items-center justify-between gap-3 px-5 py-3 border-b shrink-0" style={{ borderColor: 'var(--border)' }}>
          <div className="min-w-0">
            <h1 className="font-heading font-bold text-base truncate capitalize" data-testid="chat-project-title">
              {project ? projectTitle(project) : 'New Project'}
            </h1>
            {project && (
              <Link to={`/workspace/project/${project.lead.id}`} className="text-xs hover:underline" style={{ color: 'var(--primary-accent)' }} data-testid="pipeline-link">
                Pipeline & files →
              </Link>
            )}
          </div>
          {preview && (
            <Button variant="outline" size="sm" className="gap-2 shrink-0" onClick={() => setShowPreview(!showPreview)} data-testid="toggle-preview-btn">
              <PanelRight className="w-4 h-4" /> {showPreview ? 'Hide Preview' : 'Show Preview'}
            </Button>
          )}
        </header>

        {/* Thread */}
        <div className="flex-1 overflow-y-auto px-5 py-6" data-testid="chat-thread">
          <div className="max-w-3xl mx-auto space-y-5">
            {!leadId && (
              <div className="pt-16 text-center" data-testid="chat-welcome">
                <div className="w-14 h-14 rounded-2xl mx-auto mb-5 flex items-center justify-center" style={{ background: 'var(--tag-bg)' }}>
                  <Bot className="w-7 h-7" style={{ color: 'var(--primary-accent)' }} />
                </div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold">What do you want to build?</h2>
                <p className="mt-2 text-sm max-w-md mx-auto" style={{ color: 'var(--text-muted)' }}>
                  Describe it in one message. My agents take it from proposal to prototype to real code, tested and ready to ship.
                </p>
                <div className="mt-6 grid sm:grid-cols-3 gap-3 text-left">
                  {EXAMPLES.map((ex, i) => (
                    <button
                      key={i}
                      onClick={() => sendMessage(ex)}
                      className="p-3.5 rounded-xl border text-xs leading-relaxed transition-all hover:-translate-y-0.5 hover:shadow-md"
                      style={{ borderColor: 'var(--border)', background: 'var(--card-bg)', color: 'var(--text-secondary)' }}
                      data-testid={`example-prompt-${i}`}
                    >
                      {ex}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, idx) => (
              m.role === 'event' ? (
                <div key={m.id} className="flex justify-center" data-testid="chat-message-event">
                  <span className="px-3 py-1.5 rounded-full text-xs font-medium text-center" style={{ background: 'var(--surface-elevated)', color: 'var(--text-secondary)' }}>
                    {m.content}
                  </span>
                </div>
              ) : m.role === 'user' ? (
                <div key={m.id} className="flex justify-end" data-testid="chat-message-user">
                  <div className="max-w-[80%] px-4 py-3 rounded-2xl rounded-br-md text-sm leading-relaxed text-white whitespace-pre-wrap" style={{ background: 'var(--primary)' }}>
                    {m.content}
                  </div>
                </div>
              ) : (
                <div key={m.id} className="flex gap-3" data-testid="chat-message-assistant">
                  <div className="w-8 h-8 rounded-lg shrink-0 flex items-center justify-center mt-0.5" style={{ background: 'var(--tag-bg)' }}>
                    <Bot className="w-4 h-4" style={{ color: 'var(--primary-accent)' }} />
                  </div>
                  <div className="max-w-[80%]">
                    <div className="px-4 py-3 rounded-2xl rounded-tl-md text-sm leading-relaxed whitespace-pre-wrap" style={{ background: 'var(--surface)', color: 'var(--text-primary)' }}>
                      {m.content}
                    </div>
                    {m.action && idx === lastAssistantIdx && (
                      <ActionButton
                        actionId={m.action}
                        label={m.actionLabel || ({
                          generate_proposal: 'Generate Proposal (Free)',
                          teaser: 'Build Free Teaser Prototype',
                          full_prototype: 'Build Full Prototype',
                          dev_build: 'Start Development Build',
                          run_tests: 'Run AI QA & Tests',
                          release: 'Package Release',
                        }[m.action])}
                        project={project}
                        running={runningAction === m.action}
                        onRun={runAction}
                      />
                    )}
                  </div>
                </div>
              )
            ))}

            {/* Live agent activity */}
            {activity && (
              <div className="flex gap-3" data-testid="activity-bubble">
                <div className="w-8 h-8 rounded-lg shrink-0 flex items-center justify-center mt-0.5" style={{ background: 'var(--tag-bg)' }}>
                  <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--primary-accent)' }} />
                </div>
                <div className="max-w-[80%] px-4 py-3 rounded-2xl rounded-tl-md text-sm" style={{ background: 'var(--surface)' }}>
                  <span style={{ color: 'var(--text-secondary)' }}>{activity.label}</span>
                  {typeof activity.progress === 'number' && (
                    <div className="mt-2 flex items-center gap-2">
                      <Progress value={activity.progress} className="h-1.5 w-44" />
                      <span className="text-xs font-bold" style={{ color: 'var(--primary-accent)' }}>{activity.progress}%</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {sending && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-lg shrink-0 flex items-center justify-center" style={{ background: 'var(--tag-bg)' }}>
                  <Bot className="w-4 h-4" style={{ color: 'var(--primary-accent)' }} />
                </div>
                <div className="px-4 py-3 rounded-2xl text-sm" style={{ background: 'var(--surface)', color: 'var(--text-muted)' }}>
                  Thinking…
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>
        </div>

        {/* Artifacts bar */}
        {project && (
          <div className="px-5 pb-1 shrink-0">
            <div className="max-w-3xl mx-auto flex flex-wrap gap-2" data-testid="artifacts-bar">
              {(project.lead.ai_proposal || project.lead.ai_evaluation) && (
                <a href={`/proposal/${project.lead.id}`} target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors hover:border-orange-400"
                   style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)' }} data-testid="artifact-proposal">
                  <FileText className="w-3.5 h-3.5" style={{ color: 'var(--primary-accent)' }} /> Proposal
                </a>
              )}
              {preview && (
                <button onClick={() => setShowPreview(true)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors hover:border-orange-400"
                        style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)' }} data-testid="artifact-prototype">
                  <Globe className="w-3.5 h-3.5" style={{ color: 'var(--primary-accent)' }} /> {preview.isTeaser ? 'Teaser Prototype' : 'Full Prototype'}
                </button>
              )}
              {build?.status === 'ready' && (
                <button onClick={async () => {
                  try { downloadBlob(await buildAPI.download(build.id), 'source.zip'); } catch (e) { toast.error(apiErrorMessage(e)); }
                }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors hover:border-orange-400"
                        style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)' }} data-testid="artifact-source">
                  <Code2 className="w-3.5 h-3.5" style={{ color: 'var(--primary-accent)' }} /> Source ZIP
                </button>
              )}
              {build?.release_status === 'ready' && (
                <button onClick={async () => {
                  try { downloadBlob(await buildAPI.downloadRelease(build.id), 'release.zip'); } catch (e) { toast.error(apiErrorMessage(e)); }
                }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors hover:border-orange-400"
                        style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)' }} data-testid="artifact-release">
                  <Rocket className="w-3.5 h-3.5" style={{ color: 'var(--primary-accent)' }} /> Release Package
                </button>
              )}
            </div>
          </div>
        )}

        {/* Input */}
        <div className="px-5 py-4 shrink-0">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-end gap-2 rounded-2xl border p-2 shadow-sm" style={{ borderColor: 'var(--border-strong)', background: 'var(--card-bg)' }}>
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                placeholder={leadId ? 'Reply to your build agent…' : 'Describe what you want to build…'}
                rows={1}
                className="flex-1 resize-none bg-transparent outline-none px-2 py-2 text-sm max-h-32"
                style={{ color: 'var(--text-primary)' }}
                data-testid="chat-input"
              />
              <Button
                size="icon"
                className="rounded-xl text-white shrink-0"
                style={{ background: 'var(--primary-accent)' }}
                disabled={sending || !input.trim()}
                onClick={() => sendMessage()}
                data-testid="chat-send-btn"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-[11px] mt-1.5 text-center" style={{ color: 'var(--text-muted)' }}>
              Proposal free · Teaser free · Full prototype 150 cr · Development 500 cr · QA 200 cr · Release 100 cr
            </p>
          </div>
        </div>
      </main>

      {/* ============ PREVIEW PANEL ============ */}
      {showPreview && preview && (
        <aside className="hidden lg:flex w-[42%] max-w-[760px] flex-col border-l shrink-0" style={{ borderColor: 'var(--border)' }} data-testid="preview-panel">
          <div className="flex items-center justify-between px-4 py-2.5 border-b shrink-0" style={{ borderColor: 'var(--border)' }}>
            <span className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>
              Live Preview — {preview.isTeaser ? 'Teaser' : 'Full Prototype'}
            </span>
            <div className="flex items-center gap-1.5">
              <Button variant="ghost" size="sm" className="gap-1.5 text-xs" onClick={() => window.open(preview.url, '_blank')} data-testid="open-preview-window-btn">
                <ExternalLink className="w-3.5 h-3.5" /> New window
              </Button>
              <Button variant="ghost" size="icon" className="w-7 h-7" onClick={() => setShowPreview(false)} data-testid="close-preview-btn">
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <iframe src={preview.url} title="Prototype preview" className="flex-1 w-full bg-white" data-testid="preview-iframe" />
        </aside>
      )}

      <BuyCreditsModal open={showBuy} onOpenChange={setShowBuy} />
    </div>
  );
}
